/**
 * A serialized asset's condition and its status lifecycle.
 *
 * Status is WHERE THE THING IS IN ITS OPERATIONAL LIFE, and the doc is firm
 * that it does not replace ownership or assignment: an asset is DEPLOYED and
 * owned by HipNumber and assigned to a tenant as a rental, all at once, in
 * three different columns. So the statuses below are the physical/service
 * states, and the transition table encodes the two rules that actually bite
 * (ADR-63 section 46):
 *
 *   - a RETIRED, SOLD or DISPOSED asset cannot be brought back into service by
 *     a stray status edit (rule 4: a retired asset cannot be newly assigned);
 *     DISPOSED and SOLD are final, RETIRED may only be un-retired to IN_STOCK
 *     or moved on to DISPOSED.
 *   - the assignment-driven statuses (ASSIGNED, DEPLOYED, LOANED, RENTED,
 *     LEASED) are set by the assignment workflow, not by hand, so the
 *     operator status control does not offer them. They are still valid column
 *     values (the check constraint allows all of them) so the later assignment
 *     cut needs no migration to widen it.
 *
 * `canTransitionAssetStatus` is the one function the API refuses an operator
 * status change with and the UI builds its menu from, exactly as the ops
 * lifecycles work, so the two can never disagree.
 */

export const ASSET_STATUSES = [
  'IN_STOCK',
  'READY',
  'ASSIGNED',
  'DEPLOYED',
  'IN_TRANSIT',
  'LOANED',
  'RENTED',
  'LEASED',
  'REPAIR',
  'MAINTENANCE',
  'QUARANTINED',
  'DAMAGED',
  'LOST',
  'RETIRED',
  'SOLD',
  'DISPOSED',
] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export const ASSET_STATUS_LABEL: Readonly<Record<AssetStatus, string>> = {
  IN_STOCK: 'In stock',
  READY: 'Ready',
  ASSIGNED: 'Assigned',
  DEPLOYED: 'Deployed',
  IN_TRANSIT: 'In transit',
  LOANED: 'Loaned',
  RENTED: 'Rented',
  LEASED: 'Leased',
  REPAIR: 'Repair',
  MAINTENANCE: 'Maintenance',
  QUARANTINED: 'Quarantined',
  DAMAGED: 'Damaged',
  LOST: 'Lost',
  RETIRED: 'Retired',
  SOLD: 'Sold',
  DISPOSED: 'Disposed',
};

/**
 * Statuses an assignment sets and clears, not the operator directly. Named here
 * so the status control can exclude them and a later cut can find them in one
 * place rather than a string literal scattered across the runtime.
 */
export const ASSET_ASSIGNMENT_STATUSES: readonly AssetStatus[] = ['ASSIGNED', 'DEPLOYED', 'LOANED', 'RENTED', 'LEASED'];

/** An asset in one of these is off the books for good; nothing reactivates it by a status edit. */
export const ASSET_TERMINAL_STATUSES: readonly AssetStatus[] = ['SOLD', 'DISPOSED'];

/**
 * Allowed OPERATOR-initiated status changes. The assignment-driven statuses
 * appear as sources (an asset comes back FROM deployment) but never as
 * operator-chosen destinations - `assignableTargets` filters those out.
 */
export const ASSET_STATUS_TRANSITIONS: Readonly<Record<AssetStatus, readonly AssetStatus[]>> = {
  IN_STOCK: ['READY', 'IN_TRANSIT', 'REPAIR', 'MAINTENANCE', 'QUARANTINED', 'DAMAGED', 'LOST', 'RETIRED', 'SOLD', 'DISPOSED'],
  READY: ['IN_STOCK', 'IN_TRANSIT', 'REPAIR', 'MAINTENANCE', 'QUARANTINED', 'DAMAGED', 'LOST'],
  IN_TRANSIT: ['IN_STOCK', 'READY', 'REPAIR', 'DAMAGED', 'LOST'],
  REPAIR: ['IN_STOCK', 'READY', 'MAINTENANCE', 'QUARANTINED', 'DAMAGED', 'RETIRED', 'DISPOSED'],
  MAINTENANCE: ['IN_STOCK', 'READY', 'REPAIR', 'QUARANTINED', 'DAMAGED', 'RETIRED'],
  QUARANTINED: ['IN_STOCK', 'READY', 'REPAIR', 'MAINTENANCE', 'DAMAGED', 'RETIRED', 'DISPOSED'],
  DAMAGED: ['REPAIR', 'MAINTENANCE', 'QUARANTINED', 'RETIRED', 'DISPOSED', 'LOST'],
  LOST: ['IN_STOCK', 'RETIRED', 'DISPOSED'],
  RETIRED: ['IN_STOCK', 'DISPOSED'],
  SOLD: [],
  DISPOSED: [],
  // Assignment-driven sources: how a thing returns to inventory when a later
  // cut closes its assignment. Not operator-chosen destinations.
  ASSIGNED: ['IN_STOCK', 'READY', 'IN_TRANSIT', 'REPAIR', 'MAINTENANCE', 'DAMAGED', 'LOST'],
  DEPLOYED: ['IN_STOCK', 'READY', 'IN_TRANSIT', 'REPAIR', 'MAINTENANCE', 'DAMAGED', 'LOST'],
  LOANED: ['IN_STOCK', 'READY', 'IN_TRANSIT', 'REPAIR', 'MAINTENANCE', 'DAMAGED', 'LOST'],
  RENTED: ['IN_STOCK', 'READY', 'IN_TRANSIT', 'REPAIR', 'MAINTENANCE', 'DAMAGED', 'LOST'],
  LEASED: ['IN_STOCK', 'READY', 'IN_TRANSIT', 'REPAIR', 'MAINTENANCE', 'DAMAGED', 'LOST'],
};

export function canTransitionAssetStatus(from: AssetStatus, to: AssetStatus): boolean {
  return ASSET_STATUS_TRANSITIONS[from].includes(to);
}

/**
 * The destinations the operator status control offers from a given status:
 * the transitions above minus the assignment-driven ones, which only the
 * assignment workflow may set.
 */
export function assignableTargets(from: AssetStatus): AssetStatus[] {
  return ASSET_STATUS_TRANSITIONS[from].filter((s) => !ASSET_ASSIGNMENT_STATUSES.includes(s));
}

export function isAssetStatus(v: unknown): v is AssetStatus {
  return typeof v === 'string' && (ASSET_STATUSES as readonly string[]).includes(v);
}

export const ASSET_CONDITIONS = ['NEW', 'GOOD', 'FAIR', 'POOR', 'DAMAGED', 'UNKNOWN'] as const;
export type AssetCondition = (typeof ASSET_CONDITIONS)[number];

export const ASSET_CONDITION_LABEL: Readonly<Record<AssetCondition, string>> = {
  NEW: 'New',
  GOOD: 'Good',
  FAIR: 'Fair',
  POOR: 'Poor',
  DAMAGED: 'Damaged',
  UNKNOWN: 'Unknown',
};

export function isAssetCondition(v: unknown): v is AssetCondition {
  return typeof v === 'string' && (ASSET_CONDITIONS as readonly string[]).includes(v);
}
